import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import OnboardingBanner from '@/components/OnboardingBanner';
import {
  DashboardControls,
  useAutoRefresh,
  useDashboardWindow,
} from '@/components/DashboardControls';
import {
  formatDate,
  formatMoney,
  formatNumber,
  formatPercent,
  formatService,
} from '@/lib/format';
import {
  CHART_COLORS,
  SERIES,
  axisProps,
  gridProps,
  gridPropsVertical,
  tooltipProps,
} from '@/lib/chartTheme';
import {
  Card,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
  LiveDot,
} from '@/components/ui';
import {
  CampaignDispositions,
  type CampaignDispositionsData,
} from '@/components/CampaignDispositions';
import { useShowsUsageCosts } from '@/lib/license';
import { LiveNow } from '@/components/LiveNow';
import { IconBadgeCheck, IconClock, IconCoins, IconPhone, IconTransfer } from '@/components/icons';

interface DashboardResponse {
  // What the server actually measured over, named by the server — the only
  // label that stays right for "today" and for a calendar month.
  window?: { key: string; label: string; since: string; until: string | null };
  summary: {
    total_calls: number;
    qualified_calls: number;
    transferred_calls: number;
    dnc_calls: number;
    not_interested_calls: number;
    callback_calls: number;
    total_minutes: number;
    avg_call_seconds: number;
    avg_lead_score: number;
    qualification_rate: number;
    transfer_rate: number;
    charges: number;
    live_calls: number;
    active_bots: number;
    bot_groups: number;
    vicidial_users: number;
    assignments: number;
  };
  calls_by_day: Array<{
    day: string;
    calls: number;
    qualified: number;
    transferred: number;
    minutes: number;
  }>;
  services: Array<{ service: string; leads: number; qualified: number; avg_score: number }>;
  intents: Array<{ intent: string; calls: number }>;
  funnel: {
    calls: number;
    qualified: number;
    transfers_requested: number;
    transfers_connected: number;
    avg_wait_sec: number;
  };
  campaign_dispositions: CampaignDispositionsData;
  key_analytics: KeyAnalytics;
}

export interface KeyAnalytics {
  window_days: number;
  total_calls: number;
  answering_machines: number;
  dead_air: number;
  human_conversations: number;
  transferred: number;
  qualified: number;
  transfer_rate: number;
  transfer_rate_all_calls: number;
  machine_rate: number;
  dead_air_rate: number;
  avg_call_seconds: number;
  total_minutes: number;
  outcomes: Array<{
    key: string;
    label: string;
    tone: string;
    calls: number;
    percent: number;
  }>;
}

// Tone -> bar colour, keyed on the tone the API sends rather than on the
// outcome key, so a new outcome gets a sensible colour without a UI release.
const OUTCOME_BAR: Record<string, string> = {
  good: 'bg-emerald-500',
  info: 'bg-purple-500',
  warn: 'bg-amber-500',
  bad: 'bg-red-500',
  neutral: 'bg-ink-400',
};

/** Seconds as m:ss, because "137s" is not how anybody reads a call length. */
function formatDuration(seconds: number): string {
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

// The shared categorical palette, so a theme switch or a new accent recolours
// this chart along with every other one.
const SERVICE_COLORS = CHART_COLORS;

export default function Dashboard() {
  const [windowKey, setWindowKey] = useDashboardWindow();
  const showsCosts = useShowsUsageCosts();
  const { on: autoRefresh, setOn: setAutoRefresh, interval: refetchInterval } =
    useAutoRefresh();

  const { data, isPending, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard', windowKey],
    queryFn: () => api.get<DashboardResponse>('/dashboard', { window: windowKey }),
    refetchInterval,
    // Keep the numbers on screen while the next poll runs. Without this every
    // refresh blanks the page to a spinner, which at five-second intervals is
    // a dashboard that flickers rather than one that updates.
    placeholderData: (previous) => previous,
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const { summary, calls_by_day, services, funnel, campaign_dispositions } = data;
  const key = data.key_analytics;

  return (
    <div className="mx-auto w-full max-w-[90rem]">
      {/* Above the numbers. A trial clock and an unanswered capacity question
          are things to act on; the dashboard underneath is things to read. */}
      <OnboardingBanner />

      <PageHeader
        title="Dashboard"
        description="Your bots, your calls and what came of them."
        actions={
          <div className="flex items-center gap-3">
            {summary.live_calls > 0 && (
              <Link to="/calls/live">
                <LiveDot label={`${summary.live_calls} live`} />
              </Link>
            )}
            <DashboardControls
              window={windowKey}
              onWindow={setWindowKey}
              autoRefresh={autoRefresh}
              onAutoRefresh={setAutoRefresh}
              updatedAt={dataUpdatedAt}
            />
          </div>
        }
      />

      <div className="mb-4">
        <LiveNow />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label="Calls"
          value={formatNumber(summary.total_calls)}
          icon={<IconPhone />}
          accent="blue"
          sub={`avg ${Math.round(summary.avg_call_seconds)}s per call`}
        />
        <StatCard
          label="Qualified"
          value={formatNumber(summary.qualified_calls)}
          sub={`${formatPercent(summary.qualification_rate)} of calls`}
          tone="good"
          icon={<IconBadgeCheck />}
          accent="green"
        />
        <StatCard
          label="Transferred"
          value={formatNumber(summary.transferred_calls)}
          sub={`${formatPercent(summary.transfer_rate)} of qualified`}
          icon={<IconTransfer />}
          accent="violet"
        />
        {showsCosts ? (
          <StatCard
            label="Charges"
            value={formatMoney(summary.charges)}
            sub={`${formatNumber(summary.total_minutes)} minutes`}
            icon={<IconCoins />}
            accent="amber"
          />
        ) : (
          <StatCard label="Minutes" value={formatNumber(summary.total_minutes)} icon={<IconClock />} accent="amber" />
        )}
      </div>

      {/*
        Not four more tiles the same size as the four above.
        
        Sixteen identical cards is what this page was, and a reader given
        sixteen things of equal weight reads none of them -- the eye has
        nothing to land on first. These four describe the account's SETUP,
        which changes monthly and is checked rarely; the four above are
        yesterday's RESULT, which is why anybody opened the page. So these are
        one quiet strip rather than four loud cards.
      */}
      <div className="mt-4 rounded-xl border border-line bg-surface shadow-card">
        <div className="flex flex-wrap divide-y divide-line sm:divide-y-0 sm:divide-x">
          {[
            { label: 'Active bots', value: formatNumber(summary.active_bots) },
            { label: 'Vicidial users', value: formatNumber(summary.vicidial_users) },
            { label: 'Assignments', value: formatNumber(summary.assignments) },
            {
              label: 'Avg lead score',
              value: summary.avg_lead_score.toFixed(0),
              sub: `avg call ${Math.round(summary.avg_call_seconds)}s`,
            },
          ].map((item) => (
            <div key={item.label} className="min-w-0 flex-1 basis-1/2 px-4 py-3 sm:basis-0">
              <p className="text-2xs font-medium uppercase leading-[1.1em] tracking-[0.06em] text-ink-500 dark:text-ink-400">
                {item.label}
              </p>
              <p className="mt-0.5 text-base font-semibold tabular-nums text-ink-800 dark:text-ink-100">
                {item.value}
                {item.sub && (
                  <span className="ml-2 text-2xs font-normal text-ink-500">
                    {item.sub}
                  </span>
                )}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Key analytics.
          The numbers a dialling operation is actually run on, which are not
          the same as the tiles above. Those answer "how is the bot doing";
          these answer "what happened to the list".

          The transfer rate here is deliberately over `human_conversations`
          and not over every dial. A rate over every dial is dominated by how
          good the list is and what hour it was called at -- it moves for
          reasons that have nothing to do with the bot. The all-calls figure is
          kept underneath it, labelled, because that is the one that
          reconciles against the dialer's own report. */}
      <div className="mt-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
            Key analytics
          </h2>
          <span className="text-xs text-ink-500 dark:text-ink-400">
            last {key.window_days} days
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
          <StatCard label="Total calls" value={formatNumber(key.total_calls)} />
          <StatCard
            label="Answering machines"
            value={formatNumber(key.answering_machines)}
            sub={`${formatPercent(key.machine_rate)} of calls`}
          />
          <StatCard
            label="Dead air"
            value={formatNumber(key.dead_air)}
            sub={`${formatPercent(key.dead_air_rate)} of calls`}
          />
          <StatCard
            label="Human conversations"
            value={formatNumber(key.human_conversations)}
            sub="somebody actually spoke"
          />
          <StatCard
            label="Transfer rate"
            value={formatPercent(key.transfer_rate)}
            sub={`${formatNumber(key.transferred)} of ${formatNumber(
              key.human_conversations,
            )} conversations`}
          />
          <StatCard
            label="Avg call duration"
            value={formatDuration(key.avg_call_seconds)}
            sub={`${formatNumber(key.total_minutes)} minutes total`}
          />
        </div>

        {key.outcomes.length > 0 && (
          <Card title="Disposition history" className="mt-4">
            <div className="overflow-x-auto">
              <table className="glee-table glee-table--compact">
                <thead>
                  <tr>
                    <th>Outcome</th>
                    <th className="text-right">Calls</th>
                    <th className="text-right">Share</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {key.outcomes.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td className="text-right tabular-nums">
                        {formatNumber(row.calls)}
                      </td>
                      <td className="text-right tabular-nums text-ink-500">
                        {formatPercent(row.percent)}
                      </td>
                      <td className="w-1/3">
                        {/* A bar rather than a second number: the shape of a
                            day's dialling is the thing being read here, and
                            two columns of percentages do not show it. */}
                        <div className="h-1.5 w-full rounded-full bg-ink-100 dark:bg-ink-800">
                          <div
                            className={`h-1.5 rounded-full ${OUTCOME_BAR[row.tone] ?? OUTCOME_BAR.neutral}`}
                            style={{ width: `${Math.min(row.percent, 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Dialer-side outcome tiles. These count the disposition code actually
          written back to Vicidial, so they reconcile with the dialer's own
          reporting rather than with our internal call state. */}
      {campaign_dispositions.totals.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">
              Vicidial dispositions
            </h2>
            <span className="text-xs text-ink-500 dark:text-ink-400">
              {formatNumber(campaign_dispositions.calls)} dispositioned calls across{' '}
              {campaign_dispositions.campaigns.length}{' '}
              {campaign_dispositions.campaigns.length === 1 ? 'campaign' : 'campaigns'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
            {campaign_dispositions.totals.slice(0, 6).map((entry) => (
              <StatCard
                key={entry.code}
                label={entry.code}
                value={formatNumber(entry.calls)}
                sub={`${entry.label} · ${formatPercent(entry.pct)}`}
                tone={
                  entry.tone === 'good'
                    ? 'good'
                    : entry.tone === 'bad'
                      ? 'bad'
                      : entry.tone === 'warn'
                        ? 'warn'
                        : 'default'
                }
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card title="Call volume" className="lg:col-span-2">
          {calls_by_day.length === 0 ? (
            <p className="py-12 text-center text-sm text-ink-500">No calls in this window.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={calls_by_day} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid {...gridProps} />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatDate}
                  {...axisProps}
                />
                <YAxis {...axisProps} />
                <Tooltip
                  labelFormatter={formatDate}
                  {...tooltipProps}
                />
                <Line type="monotone" dataKey="calls" name="Calls" stroke={SERIES.primary} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="qualified" name="Qualified" stroke={SERIES.success} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="transferred" name="Transferred" stroke={SERIES.warning} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card
          title="Services detected"
          description="What the one bot is actually producing"
        >
          {services.length === 0 ? (
            <p className="py-12 text-center text-sm text-ink-500">
              No services identified yet.
            </p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={services.slice(0, 8)}
                    dataKey="leads"
                    nameKey="service"
                    innerRadius={44}
                    outerRadius={72}
                    paddingAngle={2}
                    // Recharts draws white between slices; on a dark card
                    // that is a white starburst. Match the card instead.
                    stroke="rgb(var(--surface))"
                  >
                    {services.slice(0, 8).map((_, index) => (
                      <Cell key={index} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => [value, formatService(name)]}
                    {...tooltipProps}
                  />
                </PieChart>
              </ResponsiveContainer>
              <ul className="mt-2 space-y-1">
                {services.slice(0, 6).map((service, index) => (
                  <li key={service.service} className="flex items-center gap-2 text-xs">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: SERVICE_COLORS[index % SERVICE_COLORS.length] }}
                    />
                    <span className="flex-1 truncate text-ink-700 dark:text-ink-200">
                      {formatService(service.service)}
                    </span>
                    <span className="tabular-nums text-ink-500">{service.leads}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Qualification funnel">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart
              layout="vertical"
              data={[
                { stage: 'Calls', value: funnel.calls },
                { stage: 'Qualified', value: funnel.qualified },
                { stage: 'Transfer requested', value: funnel.transfers_requested },
                { stage: 'Human connected', value: funnel.transfers_connected },
              ]}
              margin={{ left: 40, right: 16 }}
            >
              <CartesianGrid {...gridPropsVertical} />
              <XAxis type="number" {...axisProps} />
              <YAxis
                type="category"
                dataKey="stage"
                width={130}
                {...axisProps}
              />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="value" fill={SERIES.primary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
            Average wait to reach a human: {funnel.avg_wait_sec.toFixed(0)}s
          </p>
        </Card>

        <Card title="Outcomes">
          <dl className="grid grid-cols-2 gap-4">
            <Outcome label="Qualified" value={summary.qualified_calls} tone="text-emerald-600" />
            <Outcome label="Not interested" value={summary.not_interested_calls} />
            <Outcome label="Callbacks" value={summary.callback_calls} tone="text-purple-600" />
            <Outcome label="Do not call" value={summary.dnc_calls} tone="text-red-600" />
          </dl>
          <p className="mt-4 border-t border-line pt-3 text-xs text-ink-500 dark:text-ink-400">
            Every do-not-call request is suppressed locally and mirrored to the
            dialer. Those calls are never transferred.
          </p>
        </Card>
      </div>

      <div className="mt-4">
        <CampaignDispositions
          data={campaign_dispositions}
          description={`What was written back to the dialer, per campaign · ${
            data.window?.label ?? 'this period'
          }`}
        />
      </div>
    </div>
  );
}

function Outcome({
  label,
  value,
  tone = 'text-ink-900 dark:text-ink-50',
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">
        {label}
      </dt>
      <dd className={`mt-0.5 text-xl font-semibold tabular-nums ${tone}`}>
        {formatNumber(value)}
      </dd>
    </div>
  );
}
